import * as React from 'react'
import { useGetAuthzMeQuery } from '@axilon-apps/domains-ontology-ui-state'

const DEBUG_MODE_STORAGE_KEY = 'ontology-debug-mode'
const DEBUG_MODE_EVENT = 'ontology-debug-mode-change'
export const DEBUG_ACCESS_ROLE = 'global_admin'

/* eslint-disable @typescript-eslint/no-explicit-any */

// Read runtime config directly so debug preference handling stays independent
// of the state package's config snapshot.
function readRuntimeConfig(): Record<string, any> {
  if (typeof window === 'undefined') return {}
  return (window as unknown as Record<string, any>).__ONTOLOGY_CONFIG__ ?? {}
}

function isConfigEnabled(): boolean {
  return readRuntimeConfig().DEBUG_MODE === true
}

function isDemoMode(): boolean {
  return readRuntimeConfig().DEMO_MODE === true
}

function readDebugModePreference(): boolean {
  if (typeof window === 'undefined' || !('localStorage' in window)) return false

  try {
    const stored = window.localStorage.getItem(DEBUG_MODE_STORAGE_KEY)
    if (stored === 'true') return true
    if (stored === 'false') return false
    // Prophet demo / guided fixtures: default debug on so ingest logs and tool
    // traces are visible without a Settings toggle on every fresh session.
    return isDemoMode()
  } catch {
    return false
  }
}

function subscribeDebugModePreference(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined

  const handleStorage = (event: StorageEvent) => {
    if (event.key === DEBUG_MODE_STORAGE_KEY) listener()
  }

  window.addEventListener(DEBUG_MODE_EVENT, listener)
  window.addEventListener('storage', handleStorage)

  return () => {
    window.removeEventListener(DEBUG_MODE_EVENT, listener)
    window.removeEventListener('storage', handleStorage)
  }
}

interface DebugModeAccessInput {
  authzReady: boolean
  configEnabled: boolean
  roles?: readonly string[]
}

export function resolveDebugModeAccess({
  authzReady,
  configEnabled,
  roles
}: DebugModeAccessInput): boolean {
  return authzReady && configEnabled && roles?.includes(DEBUG_ACCESS_ROLE) === true
}

export function getDebugModePreference(): boolean {
  return readDebugModePreference()
}

export function setDebugMode(enabled: boolean): void {
  if (typeof window === 'undefined' || !('localStorage' in window)) return

  try {
    window.localStorage.setItem(DEBUG_MODE_STORAGE_KEY, enabled ? 'true' : 'false')
    window.dispatchEvent(new Event(DEBUG_MODE_EVENT))
  } catch {
    // Ignore storage failures; the setting is a client-side convenience.
  }
}

export function useDebugModePreference(): boolean {
  return React.useSyncExternalStore(
    subscribeDebugModePreference,
    readDebugModePreference,
    () => false
  )
}

export function useDebugModeAccess(): boolean {
  const { data: authz, isError, isFetching, isLoading } = useGetAuthzMeQuery()

  return resolveDebugModeAccess({
    authzReady: authz !== undefined && !isError && !isFetching && !isLoading,
    configEnabled: isConfigEnabled(),
    roles: authz?.roles
  })
}

export function useDebugMode(): boolean {
  const preferenceEnabled = useDebugModePreference()
  const accessEnabled = useDebugModeAccess()
  return preferenceEnabled && accessEnabled
}
